import { Box, Stack, useTheme } from "@mui/material";
import { useSetAtom } from "jotai";
import { useEffect, useLayoutEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useApplicationInformationQuery } from "web_myplann/Api/Miscellaneous/useApplicationInformationQuery";
import { DashboardSkeleton } from "web_myplann/Dashboard/Components/DashboardSkeleton";
import type { PortfolioReportingOverviewTypeEnum } from "web_myplann/Infrastructure/Api/Api";
import { AppRouting } from "web_myplann/Infrastructure/Utils/UrlUtils";
import { CarrouselDots } from "web_myplann/Shared/Components/CarrouselDots";
import type { NativePullToRefreshProps } from "web_myplann/Shared/Components/NativePullToRefresh";
import { NativePullToRefresh } from "web_myplann/Shared/Components/NativePullToRefresh";
import { useOnScreen } from "web_myplann/Shared/Hooks/useOnScreen";
import {
	type SwipeableComponentRef,
	useSwipeable,
} from "web_myplann/Shared/Hooks/useSwipeable";
import { useDashboardAnimation } from "../Hooks/useDashboardAnimation";
import { useDashboardData } from "../Hooks/useDashboardData";
import {
	getSavedDashboardIndex,
	saveDashboardIndex,
} from "../Hooks/useSavedDashboardIndex";
import { dashboardGradientAtom } from "../State/dashboardGradientAtom";
import { DASHBOARD_HERO_HEIGHT } from "../Utils/dashboardLayout";
import { DashboardAnimatedContent } from "./DashboardAnimatedContent";
import { DashboardEmptyHeader } from "./DashboardEmptyHeader";
import { DashboardGradientHeader } from "./DashboardGradientHeader";

export type DashboardDetailListItemType = {
	id: number;
	title: string;
	desc?: string;
	value: number;
	currencySign: string;
	change?: number;
	image: string;
};

type DashboardDetail = {
	list: DashboardDetailListItemType[];
	title: string;
	desc: string;
};

export type DashboardType = {
	id: number;
	overviewType: PortfolioReportingOverviewTypeEnum;
	type: string;
	value: number | null;
	itemCountWithoutValue: number;
	netValue?: number;
	currency: string;
	evaluation?: number;
	gradientFrom: string;
	gradientTo: string;
	detail?: DashboardDetail;
};

type DashboardTitleCardProps = {
	onEmptyChange?: (isEmpty: boolean) => void;
	pullToRefreshProps?: Omit<NativePullToRefreshProps, "children">;
	children?: React.ReactNode;
};

const CARD_CONTENT_HEIGHT = 224;
const GRADIENT_HEIGHT = DASHBOARD_HERO_HEIGHT;

export const DashboardTitleCard = ({
	onEmptyChange,
	pullToRefreshProps,
	children,
}: DashboardTitleCardProps) => {
	const { palette } = useTheme();
	const navigate = useNavigate();
	const setDashboardGradient = useSetAtom(dashboardGradientAtom);

	const { data: applicationInformation } = useApplicationInformationQuery();

	const carrouselRef = useRef<SwipeableComponentRef>(null);
	const colorAreaRef = useRef<HTMLDivElement>(null);

	const { dashboards, isLoading, isEmpty } = useDashboardData();

	const isCardVisible = useOnScreen(colorAreaRef, !isEmpty);

	useLayoutEffect(() => {
		onEmptyChange?.(isEmpty && !isLoading);
	}, [onEmptyChange, isEmpty, isLoading]);

	const savedIndex = Math.min(
		Math.max(getSavedDashboardIndex(), 0),
		Math.max(dashboards.length - 1, 0),
	);

	const {
		dashboard,
		activeIndex,
		isAnimating,
		previousDashboard,
		animationDirection,
		handleDashboardChange: onDashboardChange,
		calculateDirection,
	} = useDashboardAnimation(dashboards, savedIndex, !isEmpty, isCardVisible);

	useEffect(() => {
		setDashboardGradient(isEmpty ? undefined : dashboard?.gradientTo);
	}, [dashboard?.gradientTo, isEmpty, setDashboardGradient]);

	const navigateToDashboardDetail = () => {
		navigate(AppRouting.MyPlann.DashboardDetail.path(dashboard.overviewType));
	};

	const handleDashboardChange = (
		newIndex: number,
		direction: "left" | "right" | null,
	) => {
		saveDashboardIndex(newIndex);
		onDashboardChange(newIndex, direction);
	};

	const {
		handlePointerDown,
		handlePointerMove,
		handlePointerEnd,
		startChange,
		stopChange,
	} = useSwipeable(carrouselRef, false);

	const handleTooltipToggle = (open: boolean) => {
		if (open) {
			stopChange();
		} else {
			startChange();
		}
	};

	const title = applicationInformation?.hspName ?? "SAB servis";

	if (isEmpty) {
		return (
			<Stack>
				<DashboardEmptyHeader title={title} />
				<NativePullToRefresh {...pullToRefreshProps} spinnerOffset={-25}>
					{isLoading && (
						<Stack px={1.6} pt={1.6}>
							<DashboardSkeleton height={180} />
						</Stack>
					)}
					{children}
				</NativePullToRefresh>
			</Stack>
		);
	}

	return (
		<Stack sx={{ position: "relative" }}>
			<DashboardGradientHeader
				title={title}
				gradientTo={dashboard.gradientTo}
				gradientFrom={dashboard.gradientFrom}
				gradientHeight={GRADIENT_HEIGHT}
			/>

			<Box
				ref={colorAreaRef}
				sx={{
					position: "absolute",
					top: GRADIENT_HEIGHT - 1,
					left: 0,
					right: 0,
					height: "1px",
					pointerEvents: "none",
					zIndex: 0,
				}}
			/>

			<NativePullToRefresh
				{...pullToRefreshProps}
				disableContentPush
				spinnerColor={palette.darkmodeText.white}
				spinnerOffset={-30}
			>
				<Box
					sx={{
						height: CARD_CONTENT_HEIGHT,
						position: "relative",
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						overflow: "hidden",
						zIndex: 1,
						userSelect: "none",
						touchAction: "pan-y",
					}}
					onPointerDown={handlePointerDown}
					onPointerMove={handlePointerMove}
					onPointerUp={handlePointerEnd}
					onPointerCancel={handlePointerEnd}
				>
					{isLoading ? (
						<Box width="100%" pb={3} px={1.6}>
							<DashboardSkeleton height={180} />
						</Box>
					) : (
						<>
							<DashboardAnimatedContent
								dashboardData={dashboard}
								isAnimating={isAnimating}
								isPrevious={false}
								animationDirection={animationDirection}
								navigateToDashboardDetail={navigateToDashboardDetail}
								onTooltipToggle={handleTooltipToggle}
							/>

							{isAnimating && previousDashboard && (
								<DashboardAnimatedContent
									dashboardData={previousDashboard}
									isAnimating={isAnimating}
									isPrevious={true}
									animationDirection={animationDirection}
									navigateToDashboardDetail={navigateToDashboardDetail}
								/>
							)}
						</>
					)}

					<Stack
						position="absolute"
						bottom="16px"
						left="50%"
						sx={{ transform: "translateX(-50%)" }}
						zIndex={2}
					>
						<CarrouselDots
							ref={carrouselRef}
							length={dashboards.length}
							defaultValue={activeIndex}
							onClick={(val, direction) => {
								stopChange();
								const animDirection =
									direction || calculateDirection(dashboard, val);
								handleDashboardChange(val, animDirection);
								startChange();
							}}
						/>
					</Stack>
				</Box>
				{children}
			</NativePullToRefresh>
		</Stack>
	);
};
